import * as bootstrap from 'bootstrap';
import { get, post, notifier } from '../util';
import * as Highcharts from 'highcharts';

// Constants (verbatim from modal.ts)
const GYM_COLUMNS = ['chest', 'biceps', 'back', 'triceps', 'legs', 'shoulders', 'core', 'forearms', 'rock', 'cardio'] as const;
const GYM_LABELS  = ['Chest', 'Biceps', 'Back', 'Triceps', 'Legs', 'Shoulders', 'Core', 'Forearms', 'Rock Climbing', 'Cardio'] as const;
const GYM_LOCATIONS = [
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'planet',    label: 'Planet Fitness' },
    { value: 'altrock',   label: 'Altrock' },
] as const;

interface GymSessionData {
    gym_id: number;
    gym: string;
    chest: boolean; biceps: boolean; back: boolean; triceps: boolean;
    legs: boolean; shoulders: boolean; core: boolean; forearms: boolean;
    rock: boolean; cardio: boolean;
}

// Module state
let currentDate = '';
let newCardCounter = 0;
let hiddenListenerAttached = false;

export function initGymModal(): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    if (!hiddenListenerAttached) {
        modalEl.addEventListener('hidden.bs.modal', () => {
            // Defensively restore footer (idempotent — also handled by initRunsModal)
            const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
            if (footer) footer.classList.remove('d-none');
            currentDate = '';
        });
        hiddenListenerAttached = true;
    }
}

export function openGymModal(date: string): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    currentDate = date;
    newCardCounter = 0;

    get('?do=getGymFromDate&date=' + date, {}, (data: any) => {
        const sessions: GymSessionData[] = data.status === 1 ? (data.sessions || []) : [];

        const title = document.getElementById('chart-edit-modal-title');
        if (title) {
            const label = new Date(date + 'T00:00:00').toLocaleString('en', { month: 'short', day: 'numeric' });
            title.textContent = 'Gym \u2014 ' + label;
        }

        const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
        if (footer) footer.classList.add('d-none');

        const body = document.getElementById('chart-edit-modal-body');
        if (!body) return;
        body.innerHTML = '';

        const container = document.createElement('div');
        container.className = 'activity-cards-container';
        body.appendChild(container);

        if (sessions.length === 0) {
            // Empty state: pre-render one blank card
            renderGymCard(container, null);
        } else {
            for (const session of sessions) {
                renderGymCard(container, session);
            }
        }

        // + Add Session button
        const addBtn = document.createElement('button');
        addBtn.type = 'button';
        addBtn.className = 'btn btn-outline-success w-100 mt-2';
        addBtn.textContent = '+ Add Session';
        addBtn.addEventListener('click', () => {
            const card = renderGymCard(container, null);
            card.scrollIntoView({ behavior: 'smooth' });
        });
        body.appendChild(addBtn);

        // Save All button
        const saveAllBtn = document.createElement('button');
        saveAllBtn.type = 'button';
        saveAllBtn.className = 'btn btn-primary w-100 mt-2 js-gym-save-all';
        saveAllBtn.innerHTML = '<span class="js-gym-save-label">Save All</span><span class="spinner-border spinner-border-sm d-none js-gym-spinner" role="status"></span>';
        saveAllBtn.addEventListener('click', () => handleGymSaveAll(saveAllBtn));
        body.appendChild(saveAllBtn);

        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl, { backdrop: true, keyboard: true });
        modalInstance.show();
    });
}

function renderGymCard(container: HTMLElement, session: GymSessionData | null): HTMLElement {
    const cardId = session ? 'gym-card-' + session.gym_id : 'gym-card-new-' + (++newCardCounter);

    const card = document.createElement('div');
    card.className = 'card mb-3 gym-card';
    card.id = cardId;
    card.dataset.gymId = session ? String(session.gym_id) : '';
    card.dataset.type = 'gym';

    // Header with location select + delete button
    const header = document.createElement('div');
    header.className = 'card-header py-1 px-2 d-flex justify-content-between align-items-center';

    const select = document.createElement('select');
    select.className = 'form-select form-select-sm js-gym-location';

    const blankOption = document.createElement('option');
    blankOption.value = '';
    blankOption.textContent = 'Select gym...';
    select.appendChild(blankOption);

    for (const loc of GYM_LOCATIONS) {
        const opt = document.createElement('option');
        opt.value = loc.value;
        opt.textContent = loc.label;
        if (session && session.gym === loc.value) {
            opt.selected = true;
        }
        select.appendChild(opt);
    }

    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'btn btn-sm btn-link text-danger p-0 js-card-delete';
    deleteBtn.textContent = '\u{1F5D1}';
    deleteBtn.title = 'Delete';

    header.appendChild(select);
    header.appendChild(deleteBtn);
    card.appendChild(header);

    // Body with 2-column checkbox grid
    const cardBody = document.createElement('div');
    cardBody.className = 'card-body py-2 px-2';

    const grid = document.createElement('div');
    grid.style.display = 'grid';
    grid.style.gridTemplateColumns = 'repeat(2, 1fr)';
    grid.style.gap = '4px 12px';

    GYM_COLUMNS.forEach((col, i) => {
        const checkId = cardId + '-' + col;
        const wrapper = document.createElement('div');
        wrapper.className = 'form-check';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'form-check-input js-gym-' + col;
        checkbox.id = checkId;
        if (session && !!session[col]) {
            checkbox.checked = true;
        }

        const label = document.createElement('label');
        label.className = 'form-check-label';
        label.htmlFor = checkId;
        label.textContent = GYM_LABELS[i];

        wrapper.appendChild(checkbox);
        wrapper.appendChild(label);
        grid.appendChild(wrapper);
    });

    cardBody.appendChild(grid);
    card.appendChild(cardBody);

    deleteBtn.addEventListener('click', () => handleDeleteGymSession(card, session));

    container.appendChild(card);
    return card;
}

function handleDeleteGymSession(card: HTMLElement, session: GymSessionData | null): void {
    if (!window.confirm('Delete this gym session?')) return;

    // Unsaved / new card — remove immediately
    if (!session || !session.gym_id) {
        card.remove();
        return;
    }

    // Optimistic removal
    const container = card.parentElement as HTMLElement;
    const nextSibling = card.nextSibling;
    card.remove();

    post('?do=deleteGymSession&gymId=' + session.gym_id, {}, (data: any) => {
        if (data.status === 1) {
            // No chart update on delete (per user decision)
            notifier.success('Session deleted');
        } else {
            // Rollback
            container.insertBefore(card, nextSibling);
            alert(data.message || 'Failed to delete session.');
        }
    });
}

function handleGymSaveAll(saveAllBtn: HTMLButtonElement): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    const saveLabel = saveAllBtn.querySelector('.js-gym-save-label') as HTMLElement;
    const spinner = saveAllBtn.querySelector('.js-gym-spinner') as HTMLElement;

    saveAllBtn.disabled = true;
    saveLabel.classList.add('d-none');
    spinner.classList.remove('d-none');

    // Remove previous inline error
    const prevError = modalEl.querySelector('.gym-inline-error');
    if (prevError) prevError.remove();

    const cards = Array.from(document.querySelectorAll('#chart-edit-modal-body .gym-card')) as HTMLElement[];

    // Validation: every card must have a gym location selected
    for (const card of cards) {
        const gymSelect = card.querySelector('.js-gym-location') as HTMLSelectElement;
        if (!gymSelect.value) {
            saveAllBtn.disabled = false;
            saveLabel.classList.remove('d-none');
            spinner.classList.add('d-none');
            const body = document.getElementById('chart-edit-modal-body');
            if (body) {
                const errorEl = document.createElement('div');
                errorEl.className = 'text-danger small mt-2 gym-inline-error';
                errorEl.textContent = 'Please select a gym for all sessions';
                body.appendChild(errorEl);
            }
            return;
        }
    }

    // No cards — just close modal
    if (cards.length === 0) {
        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        return;
    }

    const promises: Promise<any>[] = cards.map(card => {
        const gymIdAttr = card.dataset.gymId || '';
        const gymId = gymIdAttr !== '' ? parseInt(gymIdAttr) : null;
        const gym = (card.querySelector('.js-gym-location') as HTMLSelectElement).value;

        const payload: Record<string, string | number> = { gym };
        GYM_COLUMNS.forEach(col => {
            const checkbox = card.querySelector('.js-gym-' + col) as HTMLInputElement;
            payload[col] = checkbox.checked ? 1 : 0;
        });
        if (gymId !== null && gymId > 0) payload.gym_id = gymId;

        return new Promise<any>((resolve, reject) => {
            post('?do=upsertGymSession&date=' + currentDate, payload, (data: any) => {
                data.status === 1 ? resolve(data) : reject(data.message || 'Save failed');
            });
        });
    });

    Promise.all(promises).then(results => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        // Update chart using gym_totals from last result
        const gymTotals = results[results.length - 1].gym_totals;
        if (gymTotals) {
            updateGymChartPoints(currentDate, gymTotals);
        }

        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        notifier.success('Saved');
    }).catch(errMsg => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        const body = document.getElementById('chart-edit-modal-body');
        if (body) {
            const errorEl = document.createElement('div');
            errorEl.className = 'text-danger small mt-2 gym-inline-error';
            errorEl.textContent = typeof errMsg === 'string' ? errMsg : 'An error occurred. Please try again.';
            body.appendChild(errorEl);
        }
    });
}

function updateGymChartPoints(date: string, totals: Record<string, number>): void {
    //@ts-ignore
    const chart = (Highcharts.charts as (Highcharts.Chart | undefined)[]).find((c: any) => c?.renderTo?.id === 'chart_gym');
    if (!chart) return;

    GYM_COLUMNS.forEach((col, i) => {
        const series = chart.series[i];
        if (!series) return;
        //@ts-ignore
        const pointStart = series.options.pointStart as number;
        const idx = Math.round((Date.parse(date) - pointStart) / 86400000);
        //@ts-ignore
        const point = series.data[idx];
        if (point !== undefined) {
            //@ts-ignore
            point.update(totals[col] ? 1 : null);
        }
    });
}
